import { findParentNodeClosestToPos } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import {
  TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE,
  TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE,
  TIPTAP_COLLAPSIBLE_ITEM_TYPE,
  TIPTAP_COLLAPSIBLE_LIST_TYPE,
} from './../../ricos-content';
import { TextStyle_TextAlignment } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { ExtensionProps, NodeConfig, RicosExtension } from './../../ricos-types';
import type { PmCollapsibleListNode } from './../../_shared/pm-node-types';
import { createRicosNodeExtension } from './../../plugin-commons';
import type { TiptapAdapterServices } from './../../tiptap-editor/initializeTiptapAdapter';

import { collapsibleStateManagerPlugin } from '../consts';
import { getAlignmentFromDirection } from '../utils';
import {
  CollapsibleList,
  CollapsibleListItem,
  CollapsibleListItemBody,
  CollapsibleListItemTitle,
} from './component';
import { defaultCollapsibleItem } from './defaults';
import { jumpToCollapsibleBody, keyboardShortcuts } from './keyboardShortcuts';
import {
  findNodeById,
  getCollapsibleListByItemId,
  getCollapsibleListItems,
  updateCollapsibleListAttrs,
  updateCollapsibleListItemsAttrs,
  updateListItemAttributes,
} from './utils';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [TIPTAP_COLLAPSIBLE_LIST_TYPE]: {
      /**
       * Add new item to existing collapsible list
       */
      addCollapsibleListItem: (position: number) => ReturnType;
      setCollapsibleListAttributes: (
        nodeId: string,
        attrs: Partial<PmCollapsibleListNode['attrs']>
      ) => ReturnType;
      toggleCollapsibleItem: (nodeId: string) => ReturnType;
    };
  }
}

export const tiptapExtensions = [
  createRicosNodeExtension({
    groups: ['custom-styles', 'shortcuts-enabled', 'node-container', 'footer-disabled'],
    type: 'react-node',
    reconfigure: (
      config: NodeConfig,
      _extensions: RicosExtension[],
      _props: ExtensionProps,
      settings: Record<string, unknown>,
      services: TiptapAdapterServices
    ) => {
      return {
        ...config,
        addOptions: () => {
          return { services, ...settings };
        },
      };
    },
    Component: CollapsibleList,
    name: TIPTAP_COLLAPSIBLE_LIST_TYPE,
    createExtensionConfig({ Plugin }) {
      return {
        name: this.name,
        group: 'block layoutCellContent collapsibleListItemBodyContent',
        selectable: true,
        draggable: true,
        priority: 25,
        content: 'COLLAPSIBLE_ITEM+',
        addAttributes() {
          return {
            containerData: null,
            isQapageData: null,
            direction: { default: this.options.services.context.languageDir.toUpperCase() },
            expandOnlyOne: { default: false },
            initialExpandedItems: { default: 'FIRST' },
          };
        },
        addKeyboardShortcuts() {
          const newLine = jumpToCollapsibleBody;
          const tiptapKeyboardShortcuts = keyboardShortcuts;

          const newLineShortcuts: any = [];
          this.options.services.shortcuts.shortcuts.forEach((shortcut: $TSFixMe) => {
            if (shortcut.shortcut.name === 'NewLine') {
              newLineShortcuts.push(shortcut);
            }
          });

          newLineShortcuts.forEach((newLineShortcut: $TSFixMe) => {
            const modifier = newLineShortcut.keys.macOs.modifiers[0];
            const key = newLineShortcut.keys.macOs.key;
            const shortcutKey: $TSFixMeKey<typeof tiptapKeyboardShortcuts> = modifier
              ? `${modifier}-${key}`
              : key;
            tiptapKeyboardShortcuts[shortcutKey] = newLine;
          });

          return tiptapKeyboardShortcuts;
        },
        addCommands() {
          return {
            addCollapsibleListItem:
              position =>
              ({ commands }) => {
                return commands.insertContentAt(position - 1, defaultCollapsibleItem);
              },
            setCollapsibleListAttributes:
              (nodeId: string, attrs: Partial<PmCollapsibleListNode['attrs']>) =>
              ({ state, view, commands }) => {
                updateCollapsibleListAttrs(commands, nodeId, attrs);
                updateCollapsibleListItemsAttrs(state, commands, nodeId, attrs);

                view.dispatch(state.tr);
                return true;
              },
            toggleCollapsibleItem:
              (nodeId: string) =>
              ({ state, view, commands }) => {
                const collapsibleList = getCollapsibleListByItemId(state, nodeId);
                if (!collapsibleList) {
                  return false;
                }
                const collapsibleListItem = findNodeById(state, nodeId)[0].node;
                const isListItemExpanded = collapsibleListItem.attrs.isExpanded;
                const items = getCollapsibleListItems(state, collapsibleList.attrs.id);

                if (collapsibleList.attrs.expandOnlyOne) {
                  const expandedItems = items.filter(
                    node => node.attrs.isExpanded === true && node.attrs.id !== nodeId
                  );
                  if (expandedItems.length > 0 && !isListItemExpanded) {
                    expandedItems.forEach(i =>
                      updateListItemAttributes(commands, i, { isExpanded: false })
                    );
                  }
                }

                updateListItemAttributes(commands, collapsibleListItem, {
                  isExpanded: !isListItemExpanded,
                });

                view.dispatch(state.tr);

                return true;
              },
          };
        },
        addProseMirrorPlugins() {
          return [
            new Plugin({
              key: collapsibleStateManagerPlugin,
              props: {
                decorations: ({ doc }) => {
                  const decorations: Decoration[] = [];
                  doc.descendants((node, pos) => {
                    const $pos = doc.resolve(pos);
                    const collapsibleListAncestorNode = findParentNodeClosestToPos(
                      $pos,
                      node => node.type?.name === TIPTAP_COLLAPSIBLE_LIST_TYPE
                    );

                    if (
                      node.type?.name !== TIPTAP_COLLAPSIBLE_LIST_TYPE &&
                      !collapsibleListAncestorNode
                    ) {
                      return false;
                    }

                    if (collapsibleListAncestorNode) {
                      if (
                        node?.attrs.textStyle?.textAlignment &&
                        node?.attrs.textStyle?.textAlignment !== TextStyle_TextAlignment.AUTO
                      ) {
                        return;
                      }

                      decorations.push(
                        Decoration.node(pos, pos + node.nodeSize, {
                          'data-text-align': getAlignmentFromDirection(
                            collapsibleListAncestorNode?.node.attrs.direction
                          ),
                        })
                      );
                    }
                  });
                  return DecorationSet.create(doc, decorations);
                },
              },
            }),
          ];
        },
      };
    },
  }),
  createRicosNodeExtension({
    groups: ['custom-styles', 'node-container'],
    type: 'react-node',
    reconfigure: (
      config: NodeConfig,
      _extensions: RicosExtension[],
      _props: ExtensionProps,
      settings: Record<string, unknown>
    ) => ({
      ...config,
      addOptions: () => settings,
    }),
    Component: CollapsibleListItem,
    name: TIPTAP_COLLAPSIBLE_ITEM_TYPE,
    createExtensionConfig() {
      return {
        name: this.name,
        group: TIPTAP_COLLAPSIBLE_ITEM_TYPE,
        draggable: true,
        content: 'COLLAPSIBLE_ITEM_TITLE COLLAPSIBLE_ITEM_BODY',
        addAttributes: () => ({
          isExpanded: { default: true },
        }),
      };
    },
  }),
  createRicosNodeExtension({
    groups: ['custom-styles', 'node-container'],
    type: 'react-node',
    reconfigure: (
      config: NodeConfig,
      _extensions: RicosExtension[],
      _props: ExtensionProps,
      settings: Record<string, unknown>
    ) => ({
      ...config,
      addOptions: () => settings,
    }),
    Component: CollapsibleListItemTitle,
    name: TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE,
    placeholder: {
      predicate: ({ doc, pos }) => {
        const parentNode = doc.resolve(pos).parent;
        return (
          parentNode.childCount === 1 && parentNode.type.name === TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE
        );
      },
      content: 'CollapsibleList_ShownText_Placeholder',
      alwaysVisible: true,
    },
    createExtensionConfig() {
      return {
        name: this.name,
        group: TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE,
        content: '(PARAGRAPH|collapsibleListItemTitleContent)',
      };
    },
  }),
  createRicosNodeExtension({
    groups: ['custom-styles', 'node-container'],
    type: 'react-node',
    reconfigure: (
      config: NodeConfig,
      _extensions: RicosExtension[],
      _props: ExtensionProps,
      settings: Record<string, unknown>
    ) => ({
      ...config,
      addOptions: () => settings,
    }),
    Component: CollapsibleListItemBody,
    name: TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE,
    placeholder: {
      predicate: ({ doc, pos }) => {
        const parentNode = doc.resolve(pos).parent;
        return (
          parentNode.childCount === 1 && parentNode.type.name === TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE
        );
      },
      content: 'CollapsibleList_CollapsedText_Placeholder',
      alwaysVisible: true,
    },
    createExtensionConfig() {
      return {
        name: this.name,
        group: TIPTAP_COLLAPSIBLE_ITEM_BODY_TYPE,
        content: '(PARAGRAPH|collapsibleListItemBodyContent)+',
        addAttributes: () => ({
          isExpanded: { default: true },
        }),
      };
    },
  }),
];
